import React from "react"
import Link from "next/link"
import LazyLoad from 'react-lazyload';
import { Body, H3, H4 } from "../../Typography"
import { FeatSliderTitleWrapper, SlideFeatureGraphic, SlideText, SlideWrapper } from "./FeaturesSlides.styles"
import { MarketingCarouselArrowSvg, NavigationWrapper } from "../MarketingCarousel.styles"
import { VerticalSpacing } from "../../VerticalSpacing"
import { theme } from "../../../theme/theme"
import { FTMARKETINGPAGES } from "../../../utils/featuresMarketingPage";

interface SlideFeatureSharedProps {
    onRightArrowCLick?: () => void,
    onLeftArrowCLick?: () => void,
    canTriggerRightAction?: boolean,
    canTriggerLeftAction?: boolean,
}

interface SlideFeatureTemplateProps extends SlideFeatureSharedProps {
    title: JSX.Element,
    features: JSX.Element[],
    route: string,
    desktopImg: string,
    marginTopPicture?: number
}

const SlideFeatureTemplate = ({ title, features, route, desktopImg, marginTopPicture = 0, onLeftArrowCLick, canTriggerLeftAction, onRightArrowCLick, canTriggerRightAction }: SlideFeatureTemplateProps) => <SlideWrapper>
    <div>
        <SlideFeatureGraphic>
            <VerticalSpacing top={0} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: marginTopPicture }]}>
                <LazyLoad offset={500}>
                    <Link href={route}>
                        <img src={desktopImg} />
                    </Link>
                </LazyLoad>
            </VerticalSpacing>
        </SlideFeatureGraphic>
    </div>
    <div>
        <SlideText>
            <FeatSliderTitleWrapper>
                <div onClick={() => { onLeftArrowCLick && onLeftArrowCLick() }}>
                    <MarketingCarouselArrowSvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 59.414 59.414" width="30px"
                        canTriggerAction={canTriggerLeftAction}
                    >
                        <path fill="#9CA1AA" d="M45.268 1.414 43.854 0 14.146 29.707l29.708 29.707L45.268 58 16.975 29.707z" />
                    </MarketingCarouselArrowSvg>
                </div>
                <div>
                    <Link href={route}>
                        <H3>{title}</H3>
                    </Link>
                </div>
                <div onClick={() => { onRightArrowCLick && onRightArrowCLick() }}>
                    <MarketingCarouselArrowSvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 47.255 47.255" width="30px"
                        canTriggerAction={canTriggerRightAction}
                    >
                        <path fill="#9CA1AA" d="M12.314 47.255a.999.999 0 0 1-.707-1.707l21.92-21.92-21.92-21.92A.999.999 0 1 1 13.021.294L35.648 22.92a.999.999 0 0 1 0 1.414L13.021 46.962a.999.999 0 0 1-.707.293z" />
                    </MarketingCarouselArrowSvg>
                </div>
            </FeatSliderTitleWrapper>
            <ul>
                {
                    features.map((ft, index) => <li key={index}><Body>{ft}</Body></li>)
                }
            </ul>
            <Link href={route}>
                <NavigationWrapper>
                    <div>
                        <H4>Learn more</H4>
                    </div>
                    <div>
                        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1L6.70122 6.32613C7.09959 6.6983 7.09959 7.3017 6.70122 7.67387L1 13" stroke="#2E77FF" />
                        </svg>
                    </div>
                </NavigationWrapper>
            </Link>
        </SlideText>
    </div>
</SlideWrapper>

const SlideFeaturePortfolio = ({ ...rest }: SlideFeatureSharedProps) => <SlideFeatureTemplate
    {...rest}
    title={<><strong>Get a beautiful, self-updating Portfolio page</strong></>}
    features={[
        <>Get a zero-maintenance portfolio page that's automatically updating with your new content.</>,
        <>You don't even need to set it up, Authory will create your portfolio for you.</>,
        <>Say Good-bye to an outdated portfolio page forever!</>,
    ]}
    route={FTMARKETINGPAGES[0].route}
    desktopImg={"/authory_static_files/images/homepage/sliders/features/present_desktop.png"}
/>

const SlideFeatureBackup = ({ ...rest }: SlideFeatureSharedProps) => <SlideFeatureTemplate
    {...rest}
    title={<><strong>Get an automated archive of everything you've created</strong></>}
    features={[
        <>Imagine somebody visited every piece of content you publish, copy&pasted it, and created an archive just for you. That's exactly what Authory does.</>,
        <>Authory will not only find and back up all your future pieces, but your past content too. Even if it's paywalled.</>,
        <>Regardless of what happens to the original, your copy is safe in your Authory account.</>,
    ]}
    route={FTMARKETINGPAGES[1].route}
    desktopImg={"/authory_static_files/images/homepage/sliders/features/protect_desktop.png"}
/>

const SlideFeatureAnalytics = ({ ...rest }: SlideFeatureSharedProps) => <SlideFeatureTemplate
    {...rest}
    title={<><strong>Find out how your content performs on social media</strong></>}
    features={[
        <>Ever wondered how often your articles, videos and podcasts get shared on social media?</>,
        <>Authory provides you with detailed analytics for every piece of content you've ever created.</>,
        <>Check the stats in your account anytime, or receive weekly reports via email.</>,
    ]}
    route={FTMARKETINGPAGES[2].route}
    desktopImg={"/authory_static_files/images/homepage/sliders/features/analyze_desktop.png"}
/>

const SlideFeatureTracking = ({ ...rest }: SlideFeatureSharedProps) => <SlideFeatureTemplate
    {...rest}
    title={<><strong>Know where & when your content is published</strong></>}
    features={[
        <>Whenever a new piece of yours is published, Authory will find it, add it to your account and send you an email.</>,
        <>Thanks to lightning fast filters and search, everything you have every created is at your finger tips anytime.</>,
    ]}
    route={FTMARKETINGPAGES[3].route}
    desktopImg={"/authory_static_files/images/homepage/sliders/features/track_desktop.png"}
/>

const SlideFeatureCollections = ({ ...rest }: SlideFeatureSharedProps) => <SlideFeatureTemplate
    {...rest}
    title={<><strong>Curate your content for yourself, or to share it with others</strong></>}
    features={[
        <>Organize your content in collections based on topic, types of content, sources or whatever you like.</>,
        <>Automated rules make large scale curation a piece of cake. Even 1,000s of items are no problem.</>,
        <>Share your collections with others, or set them private for your own record keeping only.</>,
    ]}
    route={FTMARKETINGPAGES[4].route}
    desktopImg={"/authory_static_files/images/homepage/sliders/features/curate_desktop.png"}
/>

const SlideFeatureNewsletters = ({ ...rest }: SlideFeatureSharedProps) => <SlideFeatureTemplate
    {...rest}
    title={<><strong>Keep your audience up-to-date with an automated newsletter</strong></>}
    features={[
        <>Authory comes with a built-in newsletter system, enabling you to turn your audience into loyal fans.</>,
        <>You can decide if your subscribers should receive a daily or weekly digest of your new pieces.</>,
        <>Forget about having to create a newsletter manually, Authory automatically sends your updates.</>,
    ]}
    route={FTMARKETINGPAGES[5].route}
    desktopImg={"/authory_static_files/images/homepage/sliders/features/distribute_desktop.png"}
/>

export const FeaturesSlides2 = [
    <SlideFeaturePortfolio />,
    <SlideFeatureBackup />,
    <SlideFeatureAnalytics />,
    <SlideFeatureTracking />,
    <SlideFeatureCollections />,
    <SlideFeatureNewsletters />,
]